import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { PdfService } from 'src/app/shared/services/pdf.service';

import { EntityService } from '@app/core';
import { PurchaseManagerAnalyticsService } from '../../analytics';
import { PurchaseOrderService } from '../../services/purchase-order.service';

@Component({
    selector: 'app-local-supplier-purchase-order-modal',
    templateUrl: './local-supplier-purchase-order-modal.component.html',
    styleUrls: ['./local-supplier-purchase-order-modal.component.scss']
})
export class LocalSupplierPurchaseOrderModalComponent {
    @Output() poGenerated = new EventEmitter<any>();

    constructor(
        private cdr: ChangeDetectorRef,
        private pdfService: PdfService,
        private purchaseOrderService: PurchaseOrderService,
        private entityService: EntityService,
        private pmAnalytics: PurchaseManagerAnalyticsService
    ) { }

    isPreparingPdf = false;
    isGeneratingPO = false;

    showModal = false;
    showPreviewModal = false; // Toggle for PO preview
    showSuccessModal = false; // Toggle for success message
    showAdditionalFields = false; // Toggle for accordion
    supplier: any = null;
    poItems: any[] = [];

    supplierAlias = '';
    poNumber = 'PO-' + Math.floor(10000 + Math.random() * 90000) + '-' + Date.now();
    poDate = new Date();
    grandTotal = 0;
    amountInWords = '';

    // Additional Fields
    additionalFields = {
        gstNumber: '',
        mobileNumber: '',
        email: '',
        billingAddress: '',
        shippingAddress: '',
        logo: null as any
    };

    entityName = '';
    entityAddress = '';

    logoPreview: string | null = null;
    isDragging = false;

    // Validation patterns
    readonly GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    readonly MOBILE_PATTERN = /^[6-9]\d{9}$/;
    readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    errors = {
        gstNumber: false,
        mobileNumber: false,
        email: false
    };

    open(supplier: any, items: any[]): void {
        console.log('Opening PO Modal for supplier:', supplier?.name || supplier?.id);
        console.log('Items:', items);
        this.supplier = supplier;
        // Handle Alias - conform to user report that 'alias' key exists
        this.supplierAlias = supplier.supplierAlias || supplier.alias || supplier.supplier_alias || supplier.name || '';

        this.poItems = items.map(item => ({
            ...item,
            _itemName: item.itemName || item.name || item.item_name || '',
            quantity: item.quantity || 1,
            rate: item.rate || item.price || 0
        }));
        this.poNumber = 'PO-' + Math.floor(10000 + Math.random() * 90000) + '-' + Date.now();
        this.poDate = new Date();
        this.resetAdditionalFields();

        // Populate GST Number - handle Array case as reported by user
        let gst = '';
        if (Array.isArray(supplier.gstNo) && supplier.gstNo.length > 0) {
            gst = supplier.gstNo[0];
        } else if (typeof supplier.gstNo === 'string') {
            gst = supplier.gstNo;
        } else {
            // Fallback for other casing
            gst = supplier.gstNumber || supplier.gst_no || supplier.gst_number || '';
        }
        if (Array.isArray(gst) && gst.length > 0) {
            gst = gst[0];
        }
        this.additionalFields.gstNumber = gst;

        // Populate Mobile Number and Email from supplier
        this.additionalFields.mobileNumber = supplier.mobile || '';
        this.additionalFields.email = supplier.email || '';

        // Populate Entity Info
        const currentEntity = this.entityService.getSingleSelectedEntity();
        if (currentEntity) {
            this.entityName = currentEntity.name || '';
            // Do not pre-fill addresses with entity address as requested
            this.additionalFields.billingAddress = '';
            this.additionalFields.shippingAddress = '';
        }

        this.calculateTotals();
        this.showModal = true;
        this.cdr.detectChanges();

        // Track PO modal open
        this.pmAnalytics.trackCreatePOOpen({
            localSupplierId: supplier?.id || '',
            localSupplierName: supplier?.name || '',
            itemCount: this.poItems.length,
            initialPOValue: this.grandTotal,
        });
    }

    resetAdditionalFields(): void {
        this.additionalFields = {
            gstNumber: '',
            mobileNumber: '',
            email: '',
            billingAddress: '',
            shippingAddress: '',
            logo: null
        };
        this.entityName = '';
        this.entityAddress = '';
        this.logoPreview = null;
        this.errors = {
            gstNumber: false,
            mobileNumber: false,
            email: false
        };
    }

    close(): void {
        this.showModal = false;
    }

    calculateTotals(): void {
        this.grandTotal = this.poItems.reduce((acc, item) => {
            const rate = item.rate || item.price || 0;
            return acc + (item.quantity * rate);
        }, 0);
        this.amountInWords = this.convertAmountToWords(this.grandTotal);
    }

    toggleAdditionalFields(): void {
        this.showAdditionalFields = !this.showAdditionalFields;
    }

    // Logo handling
    onLogoSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.processLogo(file);
        }
    }

    onLogoDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
        const file = event.dataTransfer?.files[0];
        if (file) {
            this.processLogo(file);
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragging = false;
    }

    processLogo(file: File): void {
        if (!file.type.startsWith('image/')) {
            return;
        }

        if (file.size > 2 * 1024 * 1024) { // 2MB limit
            return;
        }

        this.additionalFields.logo = file;
        const reader = new FileReader();
        reader.onload = () => {
            this.logoPreview = reader.result as string;
            this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
    }

    removeLogo(): void {
        this.additionalFields.logo = null;
        this.logoPreview = null;
        this.cdr.detectChanges();
    }

    // Validation logic
    validateGst(): void {
        if (!this.additionalFields.gstNumber) {
            this.errors.gstNumber = false;
            return;
        }
        this.errors.gstNumber = !this.GST_PATTERN.test(this.additionalFields.gstNumber.toUpperCase());
    }

    validateMobile(): void {
        if (!this.additionalFields.mobileNumber) {
            this.errors.mobileNumber = false;
            return;
        }
        this.errors.mobileNumber = !this.MOBILE_PATTERN.test(this.additionalFields.mobileNumber);
    }

    validateEmail(): void {
        if (!this.additionalFields.email) {
            this.errors.email = false;
            return;
        }
        this.errors.email = !this.EMAIL_PATTERN.test(this.additionalFields.email);
    }

    // Preview Logic
    openPreview(): void {
        this.validateGst();
        this.validateMobile();
        this.validateEmail();

        if (this.errors.gstNumber || this.errors.mobileNumber || this.errors.email) {
            console.error('Validation errors before preview:', this.errors);
            return;
        }
        this.showPreviewModal = true;
        this.cdr.detectChanges();
    }

    closePreview(): void {
        this.showPreviewModal = false;
        this.cdr.detectChanges();
    }

    convertAmountToWords(amount: number): string {
        if (amount === 0) return 'Zero';

        const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const scales = ['', 'Thousand', 'Lakh', 'Crore'];

        let words = '';
        let num = Math.floor(amount);

        const convertChunk = (n: number): string => {
            let chunk = '';
            if (n >= 100) {
                chunk += units[Math.floor(n / 100)] + ' Hundred ';
                n %= 100;
            }
            if (n >= 20) {
                chunk += tens[Math.floor(n / 10)] + ' ';
                n %= 10;
            }
            if (n >= 10) {
                chunk += teens[n - 10] + ' ';
                n = 0;
            }
            if (n > 0) {
                chunk += units[n] + ' ';
            }
            return chunk.trim();
        };

        let scaleIdx = 0;
        while (num > 0) {
            let chunk = 0;
            if (scaleIdx === 0) { // Hundreds
                chunk = num % 1000;
                num = Math.floor(num / 1000);
            } else { // Thousands, Lakhs, Crores
                chunk = num % 100;
                num = Math.floor(num / 100);
            }

            if (chunk > 0) {
                const chunkStr = convertChunk(chunk);
                words = chunkStr + ' ' + scales[scaleIdx] + ' ' + words;
            }
            scaleIdx++;
            if (scaleIdx >= scales.length && num > 0) {
                // Handle very large numbers by repeating higher scales if needed
                break;
            }
        }

        return words.trim() + ' Rupees Only';
    }

    generatePO(): void {
        this.validateGst();
        this.validateMobile();
        this.validateEmail();

        if (this.errors.gstNumber || this.errors.mobileNumber || this.errors.email) {
            console.error('Validation errors:', this.errors);
            return;
        }

        this.isGeneratingPO = true;

        const { logo, ...fieldsToSend } = this.additionalFields;

        const poData = {
            supplierId: this.supplier.id,
            supplier_id: this.supplier.id,
            supplierName: this.supplier.name,
            supplierAlias: this.supplierAlias,
            poNumber: this.poNumber,
            date: this.poDate.toISOString(),
            items: this.poItems.map(item => ({
                itemName: item.itemName || item._itemName || null,
                itemCode: item.itemCode || null,
                unit: item.unit || null,
                quantity: item.quantity,
                rate: item.rate,
                isTaxApplicable: item.isTaxApplicable ?? null,
                isFavorite: item.isFavorite ?? null,
                itemId: item.itemId || null,
                id: item.id || null,
                _id: item._id || null,
                createdAt: item.createdAt || null,
                updatedAt: item.updatedAt || null,
                supplierName: item.supplierName || null,
                supplierId: item.supplierId || null
            })),
            totalAmount: this.grandTotal,
            additionalFields: {
                ...fieldsToSend,
                logoPreview: this.logoPreview
            }
        };

        console.log('PO Generated:', poData);

        // Track generate PO click
        this.pmAnalytics.trackGeneratePOClick({
            localSupplierId: this.supplier.id,
            localSupplierName: this.supplier.name,
            finalItems: this.poItems.map(i => ({
                itemId: i.id || i._id || '',
                itemName: i._itemName || i.itemName || '',
                quantity: i.quantity || 1,
                unitPrice: i.rate || 0,
                lineTotal: (i.quantity || 1) * (i.rate || 0),
            })),
            finalPOValue: this.grandTotal,
            finalItemCount: this.poItems.length,
            totalModifications: 0,
        });

        // Call API to create PO
        this.purchaseOrderService.createPurchaseOrder(poData).subscribe(
            (response) => {
                console.log('PO Created successfully:', response);
                this.isGeneratingPO = false;
                this.poGenerated.emit(poData);

                // Track PO success
                this.pmAnalytics.trackPOResult(true, {
                    localSupplierId: this.supplier.id,
                    localSupplierName: this.supplier.name,
                    poValue: this.grandTotal,
                    itemCount: this.poItems.length,
                    responseTimeMs: 0,
                    poNumber: this.poNumber,
                });

                // Show success modal instead of closing immediately
                this.showPreviewModal = false;
                this.showModal = false;
                this.showSuccessModal = true;
                this.cdr.detectChanges();
            },
            (error) => {
                console.error('Error creating PO:', error);
                this.isGeneratingPO = false;

                // Track PO failure
                this.pmAnalytics.trackPOResult(false, {
                    localSupplierId: this.supplier.id,
                    localSupplierName: this.supplier.name,
                    poValue: this.grandTotal,
                    itemCount: this.poItems.length,
                    responseTimeMs: 0,
                    errorMessage: error?.message || 'Unknown error',
                });

                this.cdr.detectChanges();
            }
        );
    }

    sendViaWhatsApp(): void {
        const phone = this.additionalFields.mobileNumber;
        if (!phone) return;
        console.log('Sending PO via WhatsApp to:', phone);
        // Implement WhatsApp API integration here
    }

    sendViaEmail(): void {
        const email = this.additionalFields.email;
        if (!email) return;
        console.log('Sending PO via Email to:', email);
        // Implement Email API integration here
    }

    async downloadAsPDF(): Promise<void> {
        console.log('Downloading PO as PDF...');

        // Show the printable wrapper
        const wrapper = document.getElementById('printable-po-wrapper');
        if (wrapper) {
            wrapper.classList.add('preparing-pdf');
        }

        // Wait a moment for rendering
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
            await this.pdfService.downloadElementAsPDF('printable-po-wrapper', `PO-${this.poNumber}.pdf`);
        } catch (error) {
            console.error('Download failed', error);
        } finally {
            // Hide the printable wrapper
            if (wrapper) {
                wrapper.classList.remove('preparing-pdf');
            }
        }
    }

    closeSuccessModal(): void {
        this.showSuccessModal = false;
        this.resetAdditionalFields();
        this.cdr.detectChanges();
    }
}
